import {
  Button,
  IconButton,
  Menu,
  MenuItem,
  Paper,
  Stack,
  TableBody,
  TableCell,
  TableContainer,
  TableFooter,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import React, { useState } from 'react';
import { Control, FieldPath, useFormContext } from 'react-hook-form';
import { TextFieldForm } from '../../../forms/TextFieldForm';
import { IMaskNumberProps, MaskNumber } from '../../../masks/MaskNumber';
import { NormalTable } from '../../../styled';

import { MoreHoriz } from '@mui/icons-material';
import { CalculsFormNewPerteGainsProfessionnelsActuels } from '@norma/shared/constants/calculs';
import { fCurrency } from '@norma/shared/helpers/formatNumber';
import {
  IndemnitesJournalieresPercuesPendantLaPeriodeDArretRow,
  NewPrejudiceFormPerteGainProfessionnelsActuel,
  NumeroPieceValuesRow,
  PrejudiceFormPerteGainProfessionelsFormType,
  PrejudiceFormPerteGainsProfessionnelsActuelSaisieDirecte,
  PrejudiceType,
  unites,
} from '@norma/shared/types/prejudice.type';
import { Procedure } from '@norma/shared/types/procedure.type';
import { Victime } from '@norma/shared/types/victime.type';
import { useTranslation } from 'react-i18next';
import { ComputedPropsForm } from 'src/components/forms/ComputedPropsForm';
import { ExpandableTextFieldForm } from 'src/components/forms/ExpandableTextFieldForm';
import { PredefinedDatePickerForm } from 'src/components/forms/PredefinedDatePickerForm';
import { SelectFieldForm } from 'src/components/forms/SelectFieldForm';
import { getDefaultMinAndMaxDate } from 'src/constants/prejudice/validation';
import { useFormArrayUtils } from 'src/hooks/useFormArrayUtils';
import { ComputedReadFieldForm } from '../../../forms/ComputedReadFieldForm';
import { ComputedTextFieldForm } from '../../../forms/ComputedTextFieldForm';
import { FormNumeroPieceDialog } from '../FormNumeroPieceDialog';
import { RenteAccidentDuTravailDialog } from './RenteAccidentDuTravailDialog';

interface Props {
  control: Control<
    | NewPrejudiceFormPerteGainProfessionnelsActuel
    | PrejudiceFormPerteGainsProfessionnelsActuelSaisieDirecte
  >;
  procedure: Procedure;
  victime: Victime;
  prejudiceType: PrejudiceType;
  isPGPF?: boolean;
  tauxIPP: number | null;
  allNumerosPieces: NumeroPieceValuesRow[];
  dateConsolidation?: Date | undefined;
  dateLiquidation: Date | undefined;
  revenuName:
    | FieldPath<NewPrejudiceFormPerteGainProfessionnelsActuel>
    | FieldPath<PrejudiceFormPerteGainsProfessionnelsActuelSaisieDirecte>;
  formType: Exclude<
    PrejudiceFormPerteGainProfessionelsFormType,
    'DECLARATION_FISCALES'
  >;
}

export const TableIndemnitesJournalieresPercuesPendantLaPeriodeDArret: React.FC<
  Props
> = ({
  control,
  procedure,
  victime,
  prejudiceType,
  tauxIPP,
  isPGPF = false,
  allNumerosPieces,
  dateConsolidation,
  dateLiquidation,
  revenuName,
  formType,
}) => {
  const { t } = useTranslation();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [openMenuIndex, setOpenMenuIndex] = useState<number | null>(null);

  const [
    isRenteAccidentDuTravailModalOpen,
    setIsRenteAccidentDuTravailModalOpen,
  ] = useState(false);

  const { getValues, setValue } = useFormContext();

  const { duplicate, remove, fields, append } = useFormArrayUtils({
    control: control as Control<NewPrejudiceFormPerteGainProfessionnelsActuel>,
    name: 'indemnitesJournalieresPercuesPendantLaPeriodeDArret.rows',
    getValues,
    setValue,
  });

  const handleMenuOpen =
    (index: number) => (event: React.MouseEvent<HTMLElement>) => {
      setOpenMenuIndex(index);
      setAnchorEl(event.currentTarget);
    };

  const handleMenuClose = () => {
    setOpenMenuIndex(null);
    setAnchorEl(null);
  };

  const handleDelete = (index: number) => {
    remove(index);
    handleMenuClose();
  };

  const handleDuplicate = (index: number) => {
    duplicate(index);
    handleMenuClose();
  };

  const initialValuesRow: IndemnitesJournalieresPercuesPendantLaPeriodeDArretRow =
    {
      dateDebut: isPGPF ? (dateConsolidation?.toISOString() ?? null) : null,
      dateFin: isPGPF ? (dateLiquidation?.toISOString() ?? null) : null,
      duree:
        isPGPF && dateConsolidation && dateLiquidation
          ? CalculsFormNewPerteGainsProfessionnelsActuels.getDuree({
              dateDebut: dateConsolidation.toISOString(),
              dateFin: dateLiquidation.toISOString(),
            })
          : 0,
      tiersPayeur: '',
      indemniteJournalieresPercuesNet: 0,
      uniteIndemniteJournalieresPercuesNet: '',
      csgRds: 0,
      uniteCsgRds: '',
      indemnitesPercuesNetParPeriode: 0,
      csgRDSParPeriode: 0,
      numerosPieces: { rows: [] },
      commentaires: '',
    };
  const {
    minDate: { date: minDate },
    maxDate: { date: maxDate },
  } = getDefaultMinAndMaxDate({
    procedure,
    victime,
    prejudiceType,
  });

  return (
    <>
      <TableContainer component={Paper}>
        <NormalTable size="medium">
          <TableHead>
            <TableRow>
              <TableCell align="center">
                {t('numeroPiece.form.columnHeader')}
              </TableCell>
              <TableCell align="center">
                {t(
                  'prejudice.prejudicesFormTypes.NEW_PERTE_GAINS_PROFESSIONELS.fields.indemnitesJournalieresPercuesPendantLaPeriodeDArret.rows.commentaires.columnHeader',
                )}
              </TableCell>
              <TableCell align="center">
                {t(
                  'prejudice.prejudicesFormTypes.NEW_PERTE_GAINS_PROFESSIONELS.fields.indemnitesJournalieresPercuesPendantLaPeriodeDArret.rows.tiersPayeur.columnHeader',
                )}
              </TableCell>
              <TableCell align="center">
                {isPGPF
                  ? 'Période entre la consolidation et la liquidation'
                  : t(
                      'prejudice.prejudicesFormTypes.NEW_PERTE_GAINS_PROFESSIONELS.fields.indemnitesJournalieresPercuesPendantLaPeriodeDArret.rows.dateDebut.periodeColumnHeader',
                    )}
                <Stack direction="row" justifyContent="space-around">
                  <div>
                    {t(
                      'prejudice.prejudicesFormTypes.NEW_PERTE_GAINS_PROFESSIONELS.fields.indemnitesJournalieresPercuesPendantLaPeriodeDArret.rows.dateDebut.columnHeader',
                    )}
                  </div>
                  <div>
                    {t(
                      'prejudice.prejudicesFormTypes.NEW_PERTE_GAINS_PROFESSIONELS.fields.indemnitesJournalieresPercuesPendantLaPeriodeDArret.rows.dateFin.columnHeader',
                    )}
                  </div>
                </Stack>
              </TableCell>
              <TableCell align="center">
                {t(
                  'prejudice.prejudicesFormTypes.NEW_PERTE_GAINS_PROFESSIONELS.fields.indemnitesJournalieresPercuesPendantLaPeriodeDArret.rows.duree.columnHeader',
                )}
              </TableCell>
              <TableCell align="center">
                {t(
                  'prejudice.prejudicesFormTypes.NEW_PERTE_GAINS_PROFESSIONELS.fields.indemnitesJournalieresPercuesPendantLaPeriodeDArret.rows.indemniteJournalieresPercuesNet.columnHeader',
                )}
              </TableCell>
              {!isPGPF && (
                <TableCell align="center">
                  {t(
                    'prejudice.prejudicesFormTypes.NEW_PERTE_GAINS_PROFESSIONELS.fields.indemnitesJournalieresPercuesPendantLaPeriodeDArret.rows.csgRds.columnHeader',
                  )}
                </TableCell>
              )}
              <TableCell align="center">
                {t(
                  'prejudice.prejudicesFormTypes.NEW_PERTE_GAINS_PROFESSIONELS.fields.indemnitesJournalieresPercuesPendantLaPeriodeDArret.rows.indemnitesPercuesNetParPeriode.columnHeader',
                )}
              </TableCell>
              {!isPGPF && (
                <TableCell align="center">
                  {t(
                    'prejudice.prejudicesFormTypes.NEW_PERTE_GAINS_PROFESSIONELS.fields.indemnitesJournalieresPercuesPendantLaPeriodeDArret.rows.csgRDSParPeriode.columnHeader',
                  )}
                </TableCell>
              )}
            </TableRow>
          </TableHead>
          <TableBody>
            {fields.map(({ id }, index) => (
              <TableRow key={id}>
                <TableCell align="center">
                  <FormNumeroPieceDialog
                    control={control}
                    name={`indemnitesJournalieresPercuesPendantLaPeriodeDArret.rows.${index}.numerosPieces`}
                    allOtherNumeroPieces={allNumerosPieces}
                  />
                </TableCell>
                <TableCell align="center">
                  <ExpandableTextFieldForm
                    control={control}
                    name={`indemnitesJournalieresPercuesPendantLaPeriodeDArret.rows.${index}.commentaires`}
                    dialogTitle={t(
                      'prejudice.prejudicesFormTypes.NEW_PERTE_GAINS_PROFESSIONELS.fields.indemnitesJournalieresPercuesPendantLaPeriodeDArret.rows.commentaires.columnHeader',
                    )}
                  />
                </TableCell>
                <TableCell align="center">
                  <SelectFieldForm
                    control={control}
                    name={`indemnitesJournalieresPercuesPendantLaPeriodeDArret.rows.${index}.tiersPayeur`}
                    options={procedure.tiersPayeurs.map((tiersPayeur) => ({
                      label: tiersPayeur,
                      value: tiersPayeur,
                    }))}
                  />
                </TableCell>
                <TableCell align="center">
                  <Stack direction="row" justifyContent="space-around">
                    <PredefinedDatePickerForm
                      control={control}
                      name={`indemnitesJournalieresPercuesPendantLaPeriodeDArret.rows.${index}.dateDebut`}
                      label={t(
                        'prejudice.prejudicesFormTypes.NEW_PERTE_GAINS_PROFESSIONELS.fields.indemnitesJournalieresPercuesPendantLaPeriodeDArret.rows.dateDebut.label',
                      )}
                      minDate={minDate}
                      maxDate={maxDate}
                      TextFieldProps={{
                        InputLabelProps: { shrink: true },
                        sx: {
                          minWidth: 155,
                          width: 155,
                        },
                      }}
                      procedure={procedure}
                      victime={victime}
                      prejudiceType={prejudiceType}
                    />
                    <PredefinedDatePickerForm
                      control={control}
                      name={`indemnitesJournalieresPercuesPendantLaPeriodeDArret.rows.${index}.dateFin`}
                      label={t(
                        'prejudice.prejudicesFormTypes.NEW_PERTE_GAINS_PROFESSIONELS.fields.indemnitesJournalieresPercuesPendantLaPeriodeDArret.rows.dateFin.label',
                      )}
                      minDate={minDate}
                      maxDate={maxDate}
                      TextFieldProps={{
                        InputLabelProps: { shrink: true },
                        sx: {
                          minWidth: 155,
                          width: 155,
                        },
                      }}
                      procedure={procedure}
                      victime={victime}
                      prejudiceType={prejudiceType}
                    />
                  </Stack>
                </TableCell>
                <TableCell align="center">
                  <ComputedTextFieldForm
                    name={`indemnitesJournalieresPercuesPendantLaPeriodeDArret.rows.${index}.duree`}
                    watchFields={[
                      `indemnitesJournalieresPercuesPendantLaPeriodeDArret.rows.${index}.dateDebut`,
                      `indemnitesJournalieresPercuesPendantLaPeriodeDArret.rows.${index}.dateFin`,
                    ]}
                    control={control}
                    compute={([dateDebut, dateFin]) =>
                      CalculsFormNewPerteGainsProfessionnelsActuels.getDuree({
                        dateDebut,
                        dateFin,
                      })
                    }
                    InputProps={{
                      inputComponent: MaskNumber as any,
                      inputProps: {
                        numberMask: { scale: 2 },
                      } as IMaskNumberProps,
                    }}
                    editedFieldsName="editedFields"
                  />
                </TableCell>
                <TableCell align="center">
                  <Stack
                    direction="row"
                    alignItems="center"
                    justifyContent="center"
                    spacing={1}
                  >
                    <TextFieldForm
                      control={control}
                      name={`indemnitesJournalieresPercuesPendantLaPeriodeDArret.rows.${index}.indemniteJournalieresPercuesNet`}
                      InputProps={{
                        inputComponent: MaskNumber as any,
                        inputProps: {
                          suffix: '€',
                          numberMask: { scale: 2, signed: true },
                        } as IMaskNumberProps,
                      }}
                    />
                    <SelectFieldForm
                      control={control}
                      name={`indemnitesJournalieresPercuesPendantLaPeriodeDArret.rows.${index}.uniteIndemniteJournalieresPercuesNet`}
                      style={{
                        minWidth: 130,
                        width: 130,
                      }}
                      options={unites.map((unite) => ({
                        label: t(`prejudice.fields.unite.optionsPar.${unite}`),
                        value: unite,
                      }))}
                    />
                  </Stack>
                </TableCell>
                {!isPGPF && (
                  <TableCell align="center">
                    <Stack
                      direction="row"
                      alignItems="center"
                      justifyContent="center"
                      spacing={1}
                    >
                      <TextFieldForm
                        control={control}
                        name={`indemnitesJournalieresPercuesPendantLaPeriodeDArret.rows.${index}.csgRds`}
                        InputProps={{
                          inputComponent: MaskNumber as any,
                          inputProps: {
                            suffix: '€',
                            nullable: true,
                            numberMask: {
                              scale: 2,
                              signed: true,
                            },
                          } as IMaskNumberProps,
                        }}
                      />
                      <SelectFieldForm
                        control={control}
                        name={`indemnitesJournalieresPercuesPendantLaPeriodeDArret.rows.${index}.uniteCsgRds`}
                        style={{
                          minWidth: 130,
                          width: 130,
                        }}
                        options={unites.map((unite) => ({
                          label: t(
                            `prejudice.fields.unite.optionsPar.${unite}`,
                          ),
                          value: unite,
                        }))}
                      />
                    </Stack>
                  </TableCell>
                )}
                <TableCell align="center">
                  <ComputedTextFieldForm
                    name={`indemnitesJournalieresPercuesPendantLaPeriodeDArret.rows.${index}.indemnitesPercuesNetParPeriode`}
                    watchFields={[
                      `indemnitesJournalieresPercuesPendantLaPeriodeDArret.rows.${index}.uniteIndemniteJournalieresPercuesNet`,
                      `indemnitesJournalieresPercuesPendantLaPeriodeDArret.rows.${index}.duree`,
                      `indemnitesJournalieresPercuesPendantLaPeriodeDArret.rows.${index}.indemniteJournalieresPercuesNet`,
                    ]}
                    control={control}
                    compute={([
                      uniteIndemniteJournalieresPercuesNet,
                      duree,
                      indemniteJournalieresPercuesNet,
                    ]) =>
                      CalculsFormNewPerteGainsProfessionnelsActuels.getIndemnitesPercuesNetParPeriode(
                        {
                          uniteIndemniteJournalieresPercuesNet,
                          duree,
                          indemniteJournalieresPercuesNet,
                        },
                      ) || 0
                    }
                    InputProps={{
                      inputComponent: MaskNumber as any,
                      inputProps: {
                        suffix: '€',
                        numberMask: { scale: 2, signed: true },
                      } as IMaskNumberProps,
                    }}
                    editedFieldsName="editedFields"
                  />
                </TableCell>
                {!isPGPF && (
                  <TableCell align="center">
                    <ComputedTextFieldForm
                      name={`indemnitesJournalieresPercuesPendantLaPeriodeDArret.rows.${index}.csgRDSParPeriode`}
                      watchFields={[
                        `indemnitesJournalieresPercuesPendantLaPeriodeDArret.rows.${index}.uniteCsgRds`,
                        `indemnitesJournalieresPercuesPendantLaPeriodeDArret.rows.${index}.duree`,
                        `indemnitesJournalieresPercuesPendantLaPeriodeDArret.rows.${index}.csgRds`,
                      ]}
                      control={control}
                      compute={([uniteCsgRds, duree, csgRds]) =>
                        CalculsFormNewPerteGainsProfessionnelsActuels.getCsgRDSParPeriode(
                          { uniteCsgRds, duree, csgRds },
                        ) || 0
                      }
                      InputProps={{
                        inputComponent: MaskNumber as any,
                        inputProps: {
                          suffix: '€',
                          nullable: true,
                          numberMask: { scale: 2, signed: true },
                        } as IMaskNumberProps,
                      }}
                      editedFieldsName="editedFields"
                    />
                  </TableCell>
                )}
                <TableCell align="center">
                  <IconButton onClick={handleMenuOpen(index)}>
                    <MoreHoriz />
                  </IconButton>
                  <Menu
                    anchorEl={anchorEl}
                    open={openMenuIndex === index}
                    onClose={handleMenuClose}
                  >
                    <MenuItem onClick={() => handleDelete(index)}>
                      {t('forms.table.deleteRow', { index: index + 1 })}
                    </MenuItem>
                    <MenuItem onClick={() => handleDuplicate(index)}>
                      {t('forms.table.duplicateRow', { index: index + 1 })}
                    </MenuItem>
                  </Menu>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
          <TableFooter>
            <TableRow>
              <TableCell colSpan={5 + (isPGPF ? 0 : 1)} />
              <ComputedPropsForm
                control={control}
                watchFields={[
                  'indemnitesJournalieresPercuesPendantLaPeriodeDArret.rows',
                ]}
                compute={([rows]: [
                  IndemnitesJournalieresPercuesPendantLaPeriodeDArretRow[],
                ]) => ({
                  props:
                    CalculsFormNewPerteGainsProfessionnelsActuels.getIndemnitesJournalieresPercuesNetTotal(
                      {
                        rows,
                      },
                    ),
                })}
                render={(props) => {
                  const { indemnitésJournalieresPercuesNet, csgRdsTotal } =
                    props as ReturnType<
                      typeof CalculsFormNewPerteGainsProfessionnelsActuels.getIndemnitesJournalieresPercuesNetTotal
                    >;
                  return (
                    <>
                      <TableCell align="right">
                        <ComputedReadFieldForm
                          control={control}
                          watchFields={[]}
                          customValues={[indemnitésJournalieresPercuesNet]}
                          name={
                            'indemnitesJournalieresPercuesPendantLaPeriodeDArret.indemnitesJournalieresPercuesNet'
                          }
                          compute={(_, customValues) =>
                            customValues?.[0] || null
                          }
                          render={(value) => (
                            <Typography
                              sx={{
                                fontWeight: 'bold',
                              }}
                            >
                              {fCurrency(value || 0)}
                            </Typography>
                          )}
                        />
                      </TableCell>
                      {!isPGPF && (
                        <TableCell align="right">
                          <ComputedReadFieldForm
                            control={control}
                            watchFields={[]}
                            customValues={[csgRdsTotal]}
                            name={
                              'indemnitesJournalieresPercuesPendantLaPeriodeDArret.csgRdsTotal'
                            }
                            compute={(_, customValues) =>
                              customValues?.[0] || null
                            }
                            render={(value) => (
                              <Typography
                                sx={{
                                  fontWeight: 'bold',
                                }}
                              >
                                {fCurrency(value || 0)}
                              </Typography>
                            )}
                          />
                        </TableCell>
                      )}
                    </>
                  );
                }}
              />
            </TableRow>
          </TableFooter>
        </NormalTable>
      </TableContainer>
      <Button
        sx={{ alignSelf: 'center' }}
        onClick={() => {
          append(initialValuesRow);
        }}
        disabled={procedure.tiersPayeurs.length === 0}
      >
        {t('forms.table.addRow')}
      </Button>
      {isPGPF && (
        <Stack
          sx={(theme) => ({
            backgroundColor: theme.palette.secondary.light,
            borderRadius: theme.shape.borderRadius / 8,
            padding: theme.spacing(1),
          })}
          maxWidth={400}
        >
          <Typography textAlign="center" variant="subtitle1">
            {t(
              'prejudice.prejudicesFormTypes.PERTE_GAINS_PROFESSIONNELS_FUTURS.renteAccidentDuTravail.modalButtonTitle',
            )}
          </Typography>
          {isPGPF && (
            <Button
              onClick={() => {
                setIsRenteAccidentDuTravailModalOpen(true);
              }}
              sx={{ textTransform: 'none' }}
            >
              {t(
                'prejudice.prejudicesFormTypes.PERTE_GAINS_PROFESSIONNELS_FUTURS.renteAccidentDuTravail.modalButtonText',
              )}
            </Button>
          )}
        </Stack>
      )}
      {isPGPF && (
        <ComputedPropsForm
          control={control}
          watchFields={[revenuName]}
          compute={([totalRevenuDeReference]) => ({
            props: {
              totalRevenuDeReference,
            },
          })}
          render={({ totalRevenuDeReference }) => (
            <RenteAccidentDuTravailDialog
              open={isRenteAccidentDuTravailModalOpen}
              tauxIPP={tauxIPP}
              salaireAnnuelDeReference={(totalRevenuDeReference || 0) as number}
              onClose={() => setIsRenteAccidentDuTravailModalOpen(false)}
              formType={formType}
            />
          )}
        />
      )}
    </>
  );
};
